import { SupportedEventEmitter } from "@hds/events";
class WebSocketClient extends SupportedEventEmitter {
    #_wsUrl;
    #_socket;
    #_subProtocols;
    #_logger;
    #_authenticate;
    #_connectingProm;
    #_currentStatus = WebSocketClient.STATUSES.UNKNOWN;
    #_isAuthenticated = false;
    #_dataAwaitingAuthentication = [];
    #_openEvent;
    #_authenticateError;
    #_closeCode;
    #_isRefused = false;
    //Why the server closed the connection; the same numbers as the delta-server's WebSocketServer.CLOSE_CODES
    static CLOSE_CODES = Object.freeze({
        UNAUTHENTICATED: 4401,
        REFUSED: 4403,
        AUTHENTICATION_TIMEOUT: 4408
    });
    static STATUSES = Object.freeze({
        CONNECTING: Symbol.for(0),
        OPEN: Symbol.for(1),
        CLOSING: Symbol.for(2),
        CLOSED: Symbol.for(3),
        UNKNOWN: Symbol.for('unknown'),
        from: (status) => {
            if (Object.prototype.toString.call(status) === `[object String]`) {
                status = status.toLowerCase();
            }
            const possibleStatuses = Object.values(WebSocketClient.STATUSES).toSpliced(-1);
            //If status passed in was a Symbol not a String
            if (possibleStatuses.includes(status)) {
                return status;
            }
            else {
                const found = possibleStatuses.find((possibleStatus) => possibleStatus.description === status);
                if (found) {
                    return found;
                }
                else {
                    return WebSocketClient.STATUSES.UNKNOWN;
                }
            }
        }
    });
    /**
     * `authenticate(wsUrl)` is awaited on every connection and resolves the credentials for it:
     * `{ accessToken, dpopProof }` for a person, `{ accessToken }` for a machine.
     */
    constructor(wsUrl, logger, subProtocols, additionalEvents = [], authenticate) {
        super(['open', 'close', 'error', ...additionalEvents]);
        if (typeof authenticate !== 'function') {
            throw new Error('Must supply an `authenticate` function!');
        }
        this.#_wsUrl = wsUrl;
        this.#_logger = logger;
        this.#_subProtocols = subProtocols;
        this.#_authenticate = authenticate;
    }
    /*****************************************************/
    /* START ABSTRACT METHODS */
    /*****************************************************/
    processMessageEvent(message) {
        throw Error('WebSocketClient is an Abstract Class, you MUST implement processMessage()!');
    }
    /*****************************************************/
    /* END ABSTRACT METHODS */
    /* START OVERRIDABLE METHODS */
    /*****************************************************/
    //Overridable so a runtime without the browser's WebSocket can supply its own
    _createSocket(wsUrl, subProtocols) {
        //See https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/WebSocket
        return new WebSocket(wsUrl, subProtocols);
    }
    /*****************************************************/
    /* END OVERRIDABLE METHODS */
    /* START WebSocket Public Methods */
    /*****************************************************/
    get status() {
        return this.statusAsEnum.description.toUpperCase();
    }
    get statusAsEnum() {
        return WebSocketClient.STATUSES.from(this.#_currentStatus);
    }
    isConnected() {
        return this.statusAsEnum === WebSocketClient.STATUSES.OPEN;
    }
    async whenConnected() {
        return this.connect();
    }
    //Establish Connection with the server, the connection is established once the server has authenticated it
    async connect() {
        //A refused principal stays refused, so this client does not go back to the server for it
        if (this.#_isRefused) {
            throw this.#_createConnectError('Refused by the server!');
        }
        if (!this.#_connectingProm) {
            this.#_connectingProm = new Promise((resolve, reject) => {
                this.#_authenticateError = undefined;
                this.#_setSocket(this._createSocket(this.#_wsUrl, this.#_subProtocols));
                this.#_socket.addEventListener("close", this._onClose.bind(this));
                this.#_socket.addEventListener("error", this._onError.bind(this));
                this.#_socket.addEventListener("message", this._onMessage.bind(this));
                this.#_socket.addEventListener("open", this._onOpen.bind(this));
                this.#_currentStatus = WebSocketClient.STATUSES.CONNECTING;
                const waitUntilSocketConnectsOrFails = () => {
                    setTimeout(() => {
                        switch (this.statusAsEnum) {
                            case WebSocketClient.STATUSES.OPEN:
                                resolve();
                                break;
                            case WebSocketClient.STATUSES.UNKNOWN:
                            case WebSocketClient.STATUSES.CONNECTING:
                            case WebSocketClient.STATUSES.CLOSING:
                                waitUntilSocketConnectsOrFails();
                                break;
                            case WebSocketClient.STATUSES.CLOSED:
                                reject(this.#_createConnectError('Failed to connect!'));
                                break;
                        }
                    }, 5);
                }
                waitUntilSocketConnectsOrFails();
            });
        }
        return this.#_connectingProm;
    }
    //See https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/close
    close(code, reason) {
        if (this.#_socket !== undefined) {
            this.#_currentStatus = WebSocketClient.STATUSES.CLOSING;
            this.#_socket.close(code, reason);
        }
    }
    //See https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/send
    send(data) {
        if (this.#_socket !== undefined) {
            //The server honours nothing but `authenticate` until it has answered `authenticated`
            if (this.#_isAuthenticated) {
                this.#_socket.send(data);
            }
            else {
                this.#_dataAwaitingAuthentication.push(data);
            }
        }
    }
    /*****************************************************/
    /* END WebSocket Public Methods */
    /* START WebSocket Event Handlers */
    /*****************************************************/
    //Fired when a connection with a WebSocket is closed.
    //See https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/close_event
    _onClose(event) {
        this.#_currentStatus = WebSocketClient.STATUSES.CLOSED;
        this.#_setSocket(undefined);
        this.#_connectingProm = undefined;
        this.#_closeCode = event.code;
        if (event.code === WebSocketClient.CLOSE_CODES.REFUSED) {
            this.#_isRefused = true;
        }
        this.trigger('close', event);
    }
    // Fired when a connection with a WebSocket has been closed because of an error, such as when some data couldn't be sent.
    //See https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/error_event
    _onError(event) {
        this.#_currentStatus = WebSocketClient.STATUSES.CLOSED;
        this.#_setSocket(undefined);
        const newEvent = new event.constructor(event.type, event)
        newEvent.code = 1008;
        newEvent.reason = 'An unknown error was encountered';
        this.trigger('error', newEvent);
    }
    // Fired when data is received through a WebSocket.
    //See https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/message_event
    _onMessage(event) {
        if (this.#_isAuthenticated) {
            this.processMessageEvent(event);
        }
        else if (this.#_isAuthenticatedFrame(event)) {
            this.#_onAuthenticated();
        }
    }
    // Fired when a connection with a WebSocket is opened.
    //See https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/open_event
    _onOpen(event) {
        this.#_openEvent = event;
        this.#_authenticateSocket(this.#_socket);
    }
    /*****************************************************/
    /* END WebSocket Event Handlers */
    /* START Helper Methods */
    /*****************************************************/
    #_setSocket(socket) {
        this.#_socket = socket;
        this.#_isAuthenticated = false;
        this.#_dataAwaitingAuthentication = [];
    }
    async #_authenticateSocket(socket) {
        let authenticateFrame;
        try {
            const credentials = await this.#_authenticate(this.#_wsUrl);
            authenticateFrame = JSON.stringify({
                type: 'authenticate',
                accessToken: credentials.accessToken,
                dpopProof: credentials.dpopProof
            });
        }
        catch (authenticateError) {
            //Kept for connect() to reject with once the socket has closed
            this.#_authenticateError = authenticateError;
        }
        //The socket may have closed while the credentials were being fetched
        if (socket === this.#_socket) {
            if (authenticateFrame === undefined) {
                this.close();
            }
            else {
                socket.send(authenticateFrame);
            }
        }
    }
    #_isAuthenticatedFrame(event) {
        try {
            return JSON.parse(event.data).type === 'authenticated';
        }
        catch (e) {
            //Not JSON, so not the `authenticated` frame
            return false;
        }
    }
    #_onAuthenticated() {
        this.#_isAuthenticated = true;
        this.#_currentStatus = WebSocketClient.STATUSES.OPEN;
        for (const data of this.#_dataAwaitingAuthentication.splice(0)) {
            this.#_socket.send(data);
        }
        this.trigger('open', this.#_openEvent);
    }
    //`closeCode` tells a refused credential (see CLOSE_CODES) from a server that could not be reached
    #_createConnectError(message) {
        let connectError = new Error(message);
        if (this.#_authenticateError !== undefined) {
            connectError = new Error(message, { cause: this.#_authenticateError });
        }
        connectError.closeCode = this.#_closeCode;
        return connectError;
    }
    /*****************************************************/
    /* END Helper Methods */
    /*****************************************************/
    get logger() {
        return this.#_logger;
    }
}
export { WebSocketClient };
export default WebSocketClient;